// The confirmation page's auto-return countdown
// (resources/views/booking/confirmation.blade.php).
//
// A spa reception or the call centre finishes a sale while the next customer is already at
// the desk. After 30 seconds this submits the form it lives on, which discards the session
// key holding the finished basket and goes back to the homepage - so the terminal is clean
// for whoever is next without anybody having to remember to clear it.
//
// The server decides who gets a countdown (App\Support\InternalIps) and withholds the data
// attributes below from everybody else. The form itself is on every confirmation page, as
// the customer's way back; with no attributes on it, this leaves it alone. Nothing here
// checks an address.
//
// Counts against a deadline rather than decrementing a counter each tick. setInterval is
// throttled in a background tab and drifts in a foreground one, so a naive counter would
// leave a terminal sitting on a finished sale for minutes past its thirty seconds.
//
// The countdown text and the "I need more time" control are rendered hidden and revealed
// here, because neither means anything without this file. The submit button is not - it is
// the page's working no-JavaScript exit, and on a customer's page the only signposted way
// off it.

const FORM_SELECTOR = '[data-auto-return]';

// Four times a second. The displayed number only changes once a second, but a coarser tick
// would let the last second run long, and an extension would not show until the next tick.
const TICK_MS = 250;

function initForm(form)
{
    const seconds = Number(form.dataset.autoReturnSeconds);
    const extension = Number(form.dataset.autoReturnExtension);
    const maxExtensions = Number(form.dataset.autoReturnMaxExtensions);

    // A missing or nonsensical duration leaves the page exactly as the server rendered it:
    // the submit still works, and nothing counts down to a submit that never comes.
    if (! Number.isFinite(seconds) || seconds <= 0)
    {
        return;
    }

    const message = form.querySelector('[data-auto-return-message]');
    const count = form.querySelector('[data-auto-return-count]');
    const unit = form.querySelector('[data-auto-return-unit]');
    const extend = form.querySelector('[data-auto-return-extend]');

    let deadline = Date.now() + (seconds * 1000);
    let extensionsUsed = 0;
    let submitted = false;
    let timer = null;

    const render = () =>
    {
        // Rounded up, so the last whole second is displayed as 1 rather than 0 - a 0 sitting
        // on screen for a beat reads as stuck.
        const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

        if (count)
        {
            count.textContent = String(remaining);
        }

        if (unit)
        {
            unit.textContent = remaining === 1 ? 'second' : 'seconds';
        }

        return remaining;
    };

    const tick = () =>
    {
        if (render() > 0)
        {
            return;
        }

        window.clearInterval(timer);

        // The interval is cleared above, but the guard stays: requestSubmit() does not stop
        // this function being reachable, and two submissions of a form that discards session
        // state is not a thing to leave to timing.
        if (submitted)
        {
            return;
        }

        submitted = true;

        // requestSubmit() rather than submit(), so the browser runs validation and fires the
        // submit event as a real click would. Nothing listens today; something might.
        form.requestSubmit();
    };

    if (message)
    {
        message.hidden = false;
    }

    // A zero or missing cap leaves the control hidden rather than showing one that cannot do
    // anything. The server sets both numbers, so this only happens if they are changed to
    // switch the extension off.
    if (extend && Number.isFinite(extension) && extension > 0
        && Number.isFinite(maxExtensions) && maxExtensions > 0)
    {
        extend.hidden = false;

        // Pushes the deadline out rather than resetting it, so pressing it twice in quick
        // succession genuinely buys twice the time.
        //
        // Capped, because the countdown exists so a terminal nobody is standing at clears
        // itself, and an uncapped control is a way to switch that off by leaning on it. The
        // button disables itself on the last press: greyed and not-allowed (form.scss's
        // :disabled) is the feedback, rather than a press that silently does nothing.
        extend.addEventListener('click', () =>
        {
            if (extensionsUsed >= maxExtensions)
            {
                return;
            }

            extensionsUsed += 1;
            deadline += extension * 1000;

            if (extensionsUsed >= maxExtensions)
            {
                extend.disabled = true;
            }

            render();
        });
    }

    render();

    timer = window.setInterval(tick, TICK_MS);
}

document.addEventListener('DOMContentLoaded', () =>
{
    document.querySelectorAll(FORM_SELECTOR).forEach(initForm);
});
